<template>
  <div class="w-full h-full">
    <!-- Mobile admin: hub + drill-in sub-pages (driven by the ?tab query param) -->
    <div v-if="showPage && canManageOthers && isMobile" class="w-full h-full">
      <settings-hub v-if="activeTab === null" @open="openTab" />
      <Content v-else-if="activeTab === 0" />
      <Scheduling v-else-if="activeTab === 1" />
      <PaymentsSettings v-else-if="activeTab === 2" />
      <General v-else />
    </div>

    <!-- Desktop admin: sidebar-driven tabs -->
    <div v-else-if="showPage && canManageOthers" class="flex flex-col h-full pt-2 md:pt-0">
      <tabs-wrapper class="h-full w-full">
        <tab title="עמוד נחיתה" description="עיצוב ותוכן עמוד ההזמנה שהלקוחות רואים" class="md:px-4 overflow-y-auto w-full">
          <Content ref="contentRef" />
        </tab>
        <tab title="זימון תורים" description="העדפות תהליך ההזמנה — חלון זמן, ביטולים, מדיניות תורים" class="md:px-4 overflow-y-auto">
          <Scheduling ref="schedulingRef" />
        </tab>
        <tab title="תשלומים" description="חיבור סליקה, הגדרת תשלומים וחשבוניות" class="md:px-4 overflow-y-auto">
          <PaymentsSettings ref="paymentsRef" />
        </tab>
        <tab title="משתמש" description="פרטי חשבון והגדרות אישיות" class="md:px-4 overflow-y-auto">
          <General ref="generalRef" />
        </tab>

        <!-- Desktop save lives in the header end, per the desktop design guideline
             (the floating SettingsSaveBar is the mobile-only treatment). -->
        <template #header-end-0>
          <Button v-if="canEdit" label="שמירת שינויים" icon="pi pi-check" size="small"
                  :loading="contentRef?.loadingCta" :disabled="!contentRef?.dirty || contentRef?.loadingCta"
                  @click="contentRef?.saveChanges()" />
        </template>
        <template #header-end-1>
          <Button v-if="canEdit" label="שמירת שינויים" icon="pi pi-check" size="small"
                  :loading="schedulingRef?.loadingCta" :disabled="!schedulingRef?.dirty || schedulingRef?.loadingCta"
                  @click="schedulingRef?.saveChanges()" />
        </template>
        <template #header-end-2>
          <Button v-if="canEdit" label="שמירת שינויים" icon="pi pi-check" size="small"
                  :loading="paymentsRef?.loadingCta" :disabled="!paymentsRef?.dirty || paymentsRef?.loadingCta"
                  @click="paymentsRef?.saveChanges()" />
        </template>
        <template #header-end-3>
          <Button label="שמירת שינויים" icon="pi pi-check" size="small"
                  :loading="generalRef?.loadingCta" :disabled="!generalRef?.dirty || generalRef?.loadingCta"
                  @click="generalRef?.saveChanges()" />
        </template>
      </tabs-wrapper>
    </div>

    <!-- Employees without management permissions get just their own account page -->
    <div v-else-if="showPage && !canManageOthers" class="w-full h-full flex flex-col">
      <div v-if="!isMobile" class="flex-none pe-2 ps-8 pt-4 pb-2 flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-medium text-gray-800">משתמש</h2>
          <p class="text-sm text-surface-500 mt-1">פרטי חשבון והגדרות אישיות</p>
        </div>
        <Button label="שמירת שינויים" icon="pi pi-check" size="small"
                :loading="generalOnlyRef?.loadingCta" :disabled="!generalOnlyRef?.dirty || generalOnlyRef?.loadingCta"
                @click="generalOnlyRef?.saveChanges()" />
      </div>
      <div class="md:px-8 md:pt-4 overflow-y-auto h-full">
        <General ref="generalOnlyRef" />
      </div>
    </div>

    <retry-loader
      v-else
      :isErrorState="isErrorState"
      @retry-function="fetchData"
    />
  </div>
</template>

<script lang="ts" setup>
import Content from './pages/configurations/Content.vue';
import PaymentsSettings from './pages/configurations/PaymentsSettings.vue';
import Scheduling from './pages/configurations/Scheduling.vue';
import General from './pages/configurations/General.vue';
import SettingsHub from './pages/configurations/SettingsHub.vue';
import Tab from './components/admin-tabs/Tab.vue'

import {computed, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';

import {useAdminStore} from '@app/stores';
import {AdminRouteNames} from '@app/router/route-names';
import {useAdminSettingsStore} from "@app/stores/admin-settings";
import {CONFIGURATIONS_TAB_PAGES} from '@app/types/admin-page-config';
import {useResponsive} from '@app/composables/responsive';
import { trackEvent } from '@app/composables/posthog';

const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();
const route = useRoute();
const router = useRouter();
const {isMobile} = useResponsive();
const {setPage} = adminStore;

const isErrorState = ref(false);

// Refs to the settings sub-pages so the desktop header-end button can drive
// their save action (each exposes { saveChanges, loadingCta, dirty }).
const contentRef = ref();
const schedulingRef = ref();
const paymentsRef = ref();
const generalRef = ref();
const generalOnlyRef = ref();

const showPage = computed(() => Object.entries(adminStore.getBusinessSiteData || {}).length > 0 &&
  Object.entries(settingsStore.getSettings || {}).length > 0
);
const canManageOthers = computed(() => adminStore.getCanManageOthers);
const canEdit = computed(() => adminStore.getCanEdit);

// null → the hub; 0-3 → a drill-in sub-page. Deep links (?tab=N) keep working.
const activeTab = computed<number | null>(() => {
  const tabParam = route.query.tab;
  if (tabParam === undefined) return null;
  const idx = Number(tabParam);
  return !isNaN(idx) && idx >= 0 && idx < CONFIGURATIONS_TAB_PAGES.length ? idx : null;
});

const openTab = (idx: number) => {
  router.push({ query: { ...route.query, tab: String(idx) } });
};

// Keep the mobile header (title + back arrow to the hub) in sync with the open sub-page.
// The sub-page names are adminPageConfig entries, not routes (same cast Admin.vue uses).
const syncPage = () => {
  if (isMobile.value && canManageOthers.value && activeTab.value !== null)
    setPage(CONFIGURATIONS_TAB_PAGES[activeTab.value] as AdminRouteNames);
  else
    setPage(AdminRouteNames.Configurations);
};

watch([activeTab, isMobile], syncPage);

const fetchData = async () => {
  isErrorState.value = false;
  try {
    await Promise.all([
      adminStore.fetchBusinessData(),
      settingsStore.fetchBusinessSettings(),
    ]);
  } catch (e) {
    isErrorState.value = true;
    console.error(e)

  }
}

onMounted(() => {
  trackEvent('configurations_viewed');
  fetchData();
  syncPage();
});

</script>

<style lang="scss">
.p-tabview-panels {
  padding: 0 !important;
}
</style>
